"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "@i18n/provider";
import {
  getAudioState,
  pauseAudio,
  replayAudio,
  resumeAudio,
  setAudioRate,
  subscribeAudio,
} from "@voice/audioBus";
import {
  isLessonAudioSounding,
  stopLessonAudio,
  subscribeLessonAudioPlay,
  subscribeLessonAudioStop,
} from "@lesson/shared/lessonAudio";
import type { AudioState } from "@voice/audioContract";

export function useAudioState(): AudioState {
  const [state, setState] = useState<AudioState>(getAudioState);
  useEffect(() => subscribeAudio(setState), []);
  return state;
}

/**
 * Whether ANYTHING in the app is sounding, which is not the same as bus state.
 * The speech-synthesis fallback has no element for the bus to own, so it is
 * tracked by the lessonAudio compat layer and never appears in bus state.
 * Deriving the pill from bus state alone would blank it during a spoken
 * fallback, which is the regression the compat layer was fixed to prevent.
 */
export function useAnythingSounding(): boolean {
  const [sounding, setSounding] = useState(isLessonAudioSounding);
  useEffect(() => {
    const unsubPlay = subscribeLessonAudioPlay(() => setSounding(true));
    const unsubStop = subscribeLessonAudioStop(() => setSounding(false));
    return () => {
      unsubPlay();
      unsubStop();
    };
  }, []);
  return sounding;
}

// The exact delays and gradient bars AgentChatWidget's collapsed-launcher pill
// already animates via the `wave` keyframe (src/app/globals.css). Reused here so
// this is the pill that already ships gaining controls, not a second style.
const WAVE_DELAYS = [0, 80, 160, 80, 0];

// Padding, not a literal min-width, is how the visible chip stays small while the
// clickable box still clears the repo's 44px touch-target minimum (see
// HEADER_ICON_BUTTON in AgentChatWidget.tsx for the same pattern).
const TRANSPORT_BUTTON_CLASS =
  "flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full px-2 py-1 text-xs font-medium transition-colors hover:bg-[var(--wash)]";

/**
 * Meant to become the ONLY transport controls in the app, with activities
 * rendering no play, pause, replay or speed affordance of their own: the tutor
 * owns how audio sounds, the activity owns what plays and when. That boundary is
 * not enforced yet - converting the remaining call sites is later work.
 */
export default function TutorTransport() {
  const { t } = useTranslations();
  const state = useAudioState();
  const anythingSounding = useAnythingSounding();

  // Visible whenever ANY source is sounding, including speech synthesis, which has
  // no element for the bus to own. Transport controls below render only for
  // bus-owned audio, because pause, replay and rate cannot drive the speech engine.
  // Stop is the exception: stopLessonAudio silences both sources.
  if (state.status === "idle" && !anythingSounding) return null;

  const sounding = state.status === "playing" || anythingSounding;
  const label = state.status === "idle" ? t("audio.playing") : t(state.item.label);

  return (
    <div
      className="tutor-audio-pill mr-2 flex flex-wrap items-center justify-end gap-2 rounded-2xl px-3 py-1.5"
      style={{
        background: "var(--surface)",
        boxShadow: "var(--shadow-float), 0 0 0 1px var(--hairline)",
      }}
    >
      <span className="min-w-0 max-w-[7rem] flex-shrink truncate text-xs text-ink-body">
        {label}
      </span>

      {state.status !== "error" ? (
        <span className="flex flex-shrink-0 items-center gap-0.5" aria-hidden="true">
          {WAVE_DELAYS.map((delay, i) => (
            <span
              key={i}
              className="inline-block w-0.5 rounded-full"
              style={{
                height: 18,
                background: "linear-gradient(180deg, var(--coral-light) 0%, var(--coral) 100%)",
                animation: sounding ? `wave 1.1s ease-in-out ${delay}ms infinite` : "none",
                transform: sounding ? undefined : "scaleY(0.4)",
                transformOrigin: "center",
              }}
            />
          ))}
        </span>
      ) : null}

      {state.status === "playing" ? (
        <button
          type="button"
          onClick={pauseAudio}
          aria-label={t("audio.pause")}
          className={TRANSPORT_BUTTON_CLASS}
          style={{ color: "var(--coral)" }}
        >
          {t("audio.pause")}
        </button>
      ) : null}

      {state.status === "paused" ? (
        <button
          type="button"
          onClick={resumeAudio}
          aria-label={t("audio.resume")}
          className={TRANSPORT_BUTTON_CLASS}
          style={{ color: "var(--coral)" }}
        >
          {t("audio.resume")}
        </button>
      ) : null}

      {/*
       * An error pill that only renamed its button "Retry" looked almost exactly
       * like a playing one, so a clip that never sounded read as a clip that did.
       * Say it failed.
       */}
      {state.status === "error" ? (
        <span className="min-w-0 flex-shrink text-xs" style={{ color: "var(--writing-fg)" }}>
          {t("audio.error")}
        </span>
      ) : null}

      {/*
       * Retry honours `replayable` for the same reason Replay does: it re-enters
       * playAudio with the same request. Offering it over a non-replayable clip is
       * how a speed-listening rung could be heard a second time - and, before the
       * client stopped advancing on a failed clip, heard WITH the comprehension
       * questions already on screen. An activity that withholds replay owns its own
       * retry affordance; the transport must not add one behind its back.
       */}
      {state.status === "error" && state.item.replayable ? (
        <button
          type="button"
          onClick={() => void replayAudio()}
          aria-label={t("audio.retry")}
          className={TRANSPORT_BUTTON_CLASS}
          style={{ color: "var(--coral)" }}
        >
          {t("audio.retry")}
        </button>
      ) : null}

      {(state.status === "playing" || state.status === "paused") && state.item.replayable ? (
        <button
          type="button"
          onClick={() => void replayAudio()}
          aria-label={t("audio.replay")}
          className={TRANSPORT_BUTTON_CLASS}
          style={{ color: "var(--coral)" }}
        >
          {t("audio.replay")}
        </button>
      ) : null}

      {(state.status === "playing" || state.status === "paused") && !state.item.rateLocked ? (
        <button
          type="button"
          onClick={() => setAudioRate(state.rate === 1 ? 0.75 : 1)}
          aria-label={t("audio.speed")}
          className={TRANSPORT_BUTTON_CLASS}
          style={{ color: "var(--coral)" }}
        >
          {state.rate === 1 ? "1x" : "0.75x"}
        </button>
      ) : null}

      {/*
       * Stop is the exception to "transport controls only render for bus-owned
       * audio": stopLessonAudio() silences both sources (the bus clip and the
       * speech-synthesis fallback), so it belongs whenever the pill is visible at
       * all, not just while the bus itself is playing or paused.
       */}
      <button
        type="button"
        onClick={stopLessonAudio}
        aria-label={t("audio.stop")}
        className="flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-[var(--wash)]"
        style={{ color: "var(--coral)" }}
      >
        <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3" aria-hidden="true">
          <rect x="3" y="3" width="10" height="10" rx="1.5" />
        </svg>
      </button>
    </div>
  );
}
